
import { FacebookApi, FacebookFeedItem } from '@/api/FacebookApi';
import { GoogleBusinessFeedItem, GoogleMyBusinessApi } from '@/api/GoogleMyBusinessApi';
import { InstagramApi, InstagramFeedItem } from '@/api/InstagramApi';
import { LinkedInApi, LinkedInFeedItem } from '@/api/LinkedInApi';
import { GENERIC_ERROR_DESCRIPTION, SOCIAL_PLATFORMS_METADATA } from '@/constants';
import { extractApiErrorMessage, getDeletePostErrorNotification } from '@/utilities';
import { SupportedPlatform } from 'ignite360-core';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

type ManagePlatform =
  | SupportedPlatform.Instagram
  | SupportedPlatform.FacebookMessenger
  | SupportedPlatform.LinkedIn
  | SupportedPlatform.GoogleBusiness;

export interface ManagePlatformFeedItem {
  id: string;
  title: string;
  content: string;
  mediaType: string;
  thumbnailUrl: string | null;
  permalink?: string | null;
  /** Google Business only — LIVE, PROCESSING, REJECTED, … */
  state?: string | null;
  platform: ManagePlatform;
}

@Component({ name: 'manage-platform-posts-modal' })
export default class ManagePlatformPostsModal extends Vue {
  readonly instagramPlatform = SupportedPlatform.Instagram;
  readonly linkedInPlatform = SupportedPlatform.LinkedIn;
  readonly googleBusinessPlatform = SupportedPlatform.GoogleBusiness;

  @Prop({ required: true, type: Boolean })
  readonly show!: boolean;

  @Prop({ required: true, type: Object })
  readonly botIdsByPlatform!: Partial<Record<ManagePlatform, string>>;

  activeTab: ManagePlatform = SupportedPlatform.Instagram;
  isLoading = false;
  loadError: string | null = null;
  itemsByPlatform: Partial<Record<ManagePlatform, ManagePlatformFeedItem[]>> = {};
  deletingIds = new Set<string>();
  failedThumbnailIds = new Set<string>();

  platformLabel(platform: ManagePlatform): string {
    if (platform === SupportedPlatform.FacebookMessenger) {
      return 'Facebook';
    }
    if (platform === SupportedPlatform.GoogleBusiness) {
      return 'Google Business';
    }
    const meta = SOCIAL_PLATFORMS_METADATA.find((m) => m.value === platform);
    return meta?.label || 'Platform';
  }

  platformImage(platform: ManagePlatform): string {
    const meta = SOCIAL_PLATFORMS_METADATA.find((m) => m.value === platform);
    return meta?.image || '';
  }

  get availableTabs(): Array<{ value: ManagePlatform; label: string; image: string }> {
    const tabs: Array<{ value: ManagePlatform; label: string; image: string }> = [];
    if (this.botIdsByPlatform[SupportedPlatform.Instagram]) {
      tabs.push({
        value: SupportedPlatform.Instagram,
        label: this.platformLabel(SupportedPlatform.Instagram),
        image: this.platformImage(SupportedPlatform.Instagram),
      });
    }
    if (this.botIdsByPlatform[SupportedPlatform.FacebookMessenger]) {
      tabs.push({
        value: SupportedPlatform.FacebookMessenger,
        label: this.platformLabel(SupportedPlatform.FacebookMessenger),
        image: this.platformImage(SupportedPlatform.FacebookMessenger),
      });
    }
    if (this.botIdsByPlatform[SupportedPlatform.LinkedIn]) {
      tabs.push({
        value: SupportedPlatform.LinkedIn,
        label: this.platformLabel(SupportedPlatform.LinkedIn),
        image: this.platformImage(SupportedPlatform.LinkedIn),
      });
    }
    if (this.botIdsByPlatform[SupportedPlatform.GoogleBusiness]) {
      tabs.push({
        value: SupportedPlatform.GoogleBusiness,
        label: this.platformLabel(SupportedPlatform.GoogleBusiness),
        image: this.platformImage(SupportedPlatform.GoogleBusiness),
      });
    }
    return tabs;
  }

  get activeItems(): ManagePlatformFeedItem[] {
    return this.itemsByPlatform[this.activeTab] || [];
  }

  @Watch('show')
  async onShowChange(visible: boolean) {
    if (!visible) {
      this.itemsByPlatform = {};
      this.deletingIds.clear();
      this.failedThumbnailIds.clear();
      return;
    }
    const firstTab = this.availableTabs[0]?.value;
    if (firstTab) {
      this.activeTab = firstTab;
    }
    await this.loadActiveTab(true);
  }

  @Watch('activeTab')
  async onActiveTabChange() {
    if (!this.show) {
      return;
    }
    await this.loadActiveTab(false);
  }

  /** Loads posts for the active platform tab; pass true to bypass cache. */
  async loadActiveTab(force = false) {
    if (!force && this.itemsByPlatform[this.activeTab]) {
      return;
    }
    const botId = this.botIdsByPlatform[this.activeTab];
    if (!botId) {
      this.itemsByPlatform = { ...this.itemsByPlatform, [this.activeTab]: [] };
      return;
    }
    this.isLoading = true;
    this.loadError = null;
    this.failedThumbnailIds.clear();
    try {
      if (this.activeTab === SupportedPlatform.Instagram) {
        const rows = await InstagramApi.getRecentMedia(botId);
        this.itemsByPlatform = {
          ...this.itemsByPlatform,
          [this.activeTab]: rows.map((row: InstagramFeedItem) => ({
            ...row,
            platform: this.activeTab,
          })),
        };
      } else if (this.activeTab === SupportedPlatform.LinkedIn) {
        const rows = await LinkedInApi.getRecentPosts(botId);
        this.itemsByPlatform = {
          ...this.itemsByPlatform,
          [this.activeTab]: rows.map((row: LinkedInFeedItem) => ({
            ...row,
            platform: this.activeTab,
          })),
        };
      } else if (this.activeTab === SupportedPlatform.GoogleBusiness) {
        const rows = await GoogleMyBusinessApi.getRecentPosts(botId);
        this.itemsByPlatform = {
          ...this.itemsByPlatform,
          [this.activeTab]: rows.map((row: GoogleBusinessFeedItem) => ({
            ...row,
            platform: this.activeTab,
            thumbnailUrl: row.thumbnailUrl
              ? GoogleMyBusinessApi.buildProxyMediaUrl(botId, row.thumbnailUrl) || null
              : null,
          })),
        };
      } else {
        const rows = await FacebookApi.getRecentPosts(botId);
        this.itemsByPlatform = {
          ...this.itemsByPlatform,
          [this.activeTab]: rows.map((row: FacebookFeedItem) => ({
            ...row,
            platform: this.activeTab,
          })),
        };
      }
    } catch (e) {
      this.loadError = extractApiErrorMessage(e, GENERIC_ERROR_DESCRIPTION);
      this.itemsByPlatform = { ...this.itemsByPlatform, [this.activeTab]: [] };
    } finally {
      this.isLoading = false;
    }
  }

  isDeleting(item: ManagePlatformFeedItem): boolean {
    return this.deletingIds.has(item.id);
  }

  /** Asks for confirmation, then deletes the post on the connected platform. */
  async onDelete(item: ManagePlatformFeedItem) {
    const preview = (item.content || item.title || 'this post').trim().slice(0, 120);
    const platformLabel = this.platformLabel(item.platform);
    const confirmed = confirm(
      `Are you sure you want to permanently delete this ${platformLabel} post?\n\n"${preview}"`,
    );
    if (!confirmed) {
      return;
    }
    const botId = this.botIdsByPlatform[item.platform];
    if (!botId) {
      return;
    }
    this.deletingIds.add(item.id);
    try {
      if (item.platform === SupportedPlatform.Instagram) {
        await InstagramApi.deleteMedia(botId, item.id);
      } else if (item.platform === SupportedPlatform.LinkedIn) {
        await LinkedInApi.deletePost(botId, item.id);
      } else if (item.platform === SupportedPlatform.GoogleBusiness) {
        await GoogleMyBusinessApi.deletePost(botId, item.id);
      } else {
        await FacebookApi.deletePost(botId, item.id);
      }
      const remaining = (this.itemsByPlatform[item.platform] || []).filter(
        (row) => row.id !== item.id,
      );
      this.itemsByPlatform = { ...this.itemsByPlatform, [item.platform]: remaining };
      this.$notify.success('Post deleted successfully');
      this.$emit('deleted', item);
    } catch (e) {
      const { title, description } = getDeletePostErrorNotification(e, GENERIC_ERROR_DESCRIPTION);
      this.$notify.error({ title, description });
      if (
        item.platform === SupportedPlatform.Instagram &&
        /2207085|known meta api issue/i.test(description) &&
        item.permalink
      ) {
        const openInInstagram = confirm(
          'Instagram API delete is currently unavailable.\n\nOpen this post in Instagram to delete it manually?',
        );
        if (openInInstagram) {
          window.open(item.permalink, '_blank', 'noopener,noreferrer');
        }
      }
    } finally {
      this.deletingIds.delete(item.id);
    }
  }

  /** Hides broken thumbnail URLs and falls back to the placeholder. */
  onThumbnailError(item: ManagePlatformFeedItem) {
    this.failedThumbnailIds.add(item.id);
    this.failedThumbnailIds = new Set(this.failedThumbnailIds);
  }

  mediaTypeUpper(item: ManagePlatformFeedItem): string {
    return String(item.mediaType || '').toUpperCase();
  }

  isVideoMedia(item: ManagePlatformFeedItem): boolean {
    const t = this.mediaTypeUpper(item);
    return t === 'VIDEO' || t === 'REELS';
  }

  isCarouselMedia(item: ManagePlatformFeedItem): boolean {
    return this.mediaTypeUpper(item) === 'CAROUSEL_ALBUM';
  }

  captionPreview(item: ManagePlatformFeedItem): string {
    const text = (item.content || item.title || '').trim();
    return text || 'Post';
  }

  shouldShowTitleLine(item: ManagePlatformFeedItem): boolean {
    if (item.platform === SupportedPlatform.FacebookMessenger) {
      return false;
    }
    const title = (item.title || '').trim();
    const content = (item.content || '').trim();
    if (!title) {
      return false;
    }
    return title !== content;
  }

  /** Human-readable Google Business post lifecycle state from the API. */
  formatGooglePostState(state: string): string {
    const labels: Record<string, string> = {
      LIVE: 'Live',
      PROCESSING: 'Processing',
      REJECTED: 'Rejected',
      SCHEDULED: 'Scheduled',
      RECURRING: 'Recurring',
    };
    return labels[state] || state;
  }

  /** Tailwind classes for the Google post state badge. */
  googlePostStateClass(state: string): string {
    const classes: Record<string, string> = {
      LIVE: 'bg-green-100 text-green-800',
      PROCESSING: 'bg-amber-100 text-amber-800',
      REJECTED: 'bg-red-100 text-red-800',
      SCHEDULED: 'bg-blue-100 text-blue-800',
      RECURRING: 'bg-purple-100 text-purple-800',
    };
    return classes[state] || 'bg-gray-100 text-gray-700';
  }

  /** Tooltip for the open-link action — warns when the post is not yet visible on Maps. */
  googlePostOpenTitle(item: ManagePlatformFeedItem): string {
    if (item.platform !== this.googleBusinessPlatform || !item.state) {
      return 'Open post';
    }
    if (item.state === 'PROCESSING') {
      return 'Open post (may not work until Google finishes processing)';
    }
    if (item.state === 'REJECTED') {
      return 'Open post (rejected by Google — likely not visible on Maps)';
    }
    return 'Open post';
  }
}
